import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpInterceptorFn, HttpRequest } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { EMPTY, throwError } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'
import { ToastrService } from './toastr.service'

/**
 * Intercepts outgoing HTTP requests and adds a token to authorize it
 * Also handles Error codes via a toastr service
 */
@Injectable({
  providedIn: 'root'
})
export class TokenInterceptor implements HttpInterceptor {
  /** Creates a new instances of the interceptor */
  constructor(private auth: AuthService, private toastr: ToastrService, private router: Router) {}
  /**
   * Determines whether a request should be intercepted, cloned with a token or not
   * @param request HttpRequest
   * @returns boolean
   */
  private shouldInterceptToken(request: HttpRequest<any>): boolean {
    return ![
      request.url.startsWith(`${environment.baseContactlessApi}v3/plan/contactless/auth/login`),
      request.url === `${environment.baseContactlessApi}v3/plan/contactless/auth` && request.method === 'GET',
      request.url.startsWith('https://maps.googleapis.com/maps/api/timezone'),
      request.url.startsWith('https://mt.events/shorten'),
      request.url.startsWith('/api/mjml') // Exclude MJML API calls from token interceptor
    ].some((o) => o)
  }

  /**
   * Redirects to auth when the BFF session is unusable.
   * Uses sign-out so invite deep-link `email` + `workspace_id` on the current URL are attached to auth.
   */
  private _redirectToLogin(): void {
    this.auth.signOutWithoutSubscription()
  }

  /**
   * Intercepts an http requests, clones it, adds a token to it's headers, handles it's errors
   * @param request Http request
   * @param next next http handler
   * @returns Observable<HttpEvent<any>>
   */
  intercept(request: HttpRequest<any>, next: HttpHandler) {
    // If we shouldn't intercept, just pass the request through
    if (!this.shouldInterceptToken(request)) {
      return next.handle(request)
    }

    // BFF mode: obtain token from backend session (in-memory only) and attach it.
    return this.auth.getAccessToken$().pipe(
      switchMap((accessToken) => {
        // Respect existing Authorization header (e.g., if another interceptor already set it)
        const alreadyHasAuth = request.headers.has('Authorization')

        const modifiedRequest = request.clone({
          setHeaders: {
            ...(accessToken && !alreadyHasAuth && { Authorization: `Bearer ${accessToken}` })
          }
        })

        return next.handle(modifiedRequest).pipe(
          /**
           * Catch an error and handle it
           */
          catchError((error: HttpErrorResponse) => {
            // 401 flow: refresh first; only if that fails (or retry still 401) clear + redirect.
            if (error.status === 401 && !request.headers.has('X-BFF-Retry')) {
              return this.auth.getAccessToken$({ force: true }).pipe(
                switchMap((fresh) => {
                  if (!fresh) {
                    // Refresh failed → now clear session and redirect to auth (invite-aware).
                    this._redirectToLogin()
                    return EMPTY
                  }
                  const retried = request.clone({
                    setHeaders: {
                      Authorization: `Bearer ${fresh}`,
                      'X-BFF-Retry': '1'
                    }
                  })
                  return next.handle(retried).pipe(
                    catchError((retryError: HttpErrorResponse) => {
                      // Fresh token still unauthorized → session unusable; clear + redirect.
                      if (retryError.status === 401) {
                        this._redirectToLogin()
                        return EMPTY
                      }
                      return throwError(() => retryError)
                    })
                  )
                })
              )
            }

            /** Error status code between 400/ 500 */
            if (error.status >= 400 && error.status < 500) {
              let message = ''
              // if we have a string error, display it as message
              if (typeof error.error === 'string') {
                message = error.error
              } else {
                // Otherwise loop through each property in th error an concat them to a string
                Object.keys(error.error).forEach((key) => {
                  // Loop through each property key
                  for (let i = 0; i < error.error[key]?.length; i++) {
                    message = message + error.error[key][i]
                  }
                })
              }
              // If we have the email rate limit header (Multiple failed logins)
              if (error.headers.has('email-ratelimit-remaining')) {
                message = message + '\r\n - ' + error.headers.get('email-ratelimit-remaining') + ' attempt(s) remaining'
              }
              // Check if error toast should be bypassed via context token or hardcoded conditions
              const shouldBypass =
                request.context.get(BypassErrorToast) ||
                (error.url === `${environment.planningApi}workspaces/legal-info?workspace_id=${this.auth?.currentParentWorkspace?.id}` && error.status === 404)

              if (!shouldBypass) {
                this.toastr.error('Error ', `${message}`)
              }
            }
            // throw the complete error
            return throwError(() => error)
          })
        )
      })
    )
  }
}

export const TokenInterceptorFn: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService)
  const toastr = inject(ToastrService)
  const router = inject(Router)

  // Convert HttpHandlerFn to HttpHandler
  const handler: HttpHandler = {
    handle: (req: HttpRequest<any>) => next(req)
  }

  const interceptor = new TokenInterceptor(auth, toastr, router)
  return interceptor.intercept(req, handler)
}
